import React, { createContext, useContext } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

const noopExecuteRecaptcha = () => Promise.resolve(null);

const RecaptchaContext = createContext({
  executeRecaptcha: noopExecuteRecaptcha,
});

/**
 * Inner component that runs inside GoogleReCaptchaProvider and bridges
 * the library's hook to our context (only used when RECAPTCHA_ENABLED is true).
 */
function RecaptchaContextBridge({ children }) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const value = {
    executeRecaptcha: executeRecaptcha || noopExecuteRecaptcha,
  };
  return (
    <RecaptchaContext.Provider value={value}>
      {children}
    </RecaptchaContext.Provider>
  );
}

/**
 * When RECAPTCHA_ENABLED is true: loads the reCAPTCHA script and provides real executeRecaptcha.
 * When RECAPTCHA_ENABLED is false: does NOT load the script; provides a no-op executeRecaptcha.
 */
export function ConditionalRecaptchaProvider({ children }) {
  if (RECAPTCHA_ENABLED && RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
        container={{
          parameters: {
            badge: "bottomright",
            theme: "dark",
          },
        }}
      >
        <RecaptchaContextBridge>{children}</RecaptchaContextBridge>
      </GoogleReCaptchaProvider>
    );
  }

  return (
    <RecaptchaContext.Provider value={{ executeRecaptcha: noopExecuteRecaptcha }}>
      {children}
    </RecaptchaContext.Provider>
  );
}

export function useRecaptcha() {
  return useContext(RecaptchaContext);
}
